import {Routes} from '@angular/router';
import {AuthGuard} from './shared/services/auth/auth.guard';
import {LoggedInRedirectGuard} from './shared/services/auth/logged-in-redirect.guard';
import {SeoData} from './shared/seo/seo.model';
import {faqPageJsonLd, HOME_FAQ, PRICING_FAQ, SUPPORT_FAQ, TEMPLATE_FAQ, WIDGET_FAQ} from './shared/faq/faq-pool';

const notFoundSeo: SeoData = {
  title: 'Page Not Found — Notion Habit Heroes',
  description: 'The page you are looking for does not exist.',
  noindex: true,
};

export const softwareAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  'name': 'Notion Habit Heroes',
  'alternateName': 'Habit Heroes',
  'description': 'A social habit tracker that turns your Notion habit tracker into a gamified experience: automatic streaks, competitions with friends, and 1-on-1 duels that Notion formulas can\'t compute.',
  'applicationCategory': 'LifestyleApplication',
  'operatingSystem': 'Web',
  'url': 'https://notionhabitheroes.com/',
  'sameAs': ['https://www.notion.com/connections/nh2', 'https://www.notion.com/templates/habitheroes'],
  'publisher': {
    '@type': 'Organization',
    'name': 'Notion Habit Heroes',
    'url': 'https://notionhabitheroes.com/',
  },
  'offers': [
    {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'EUR',
      'name': 'Free',
      'availability': 'https://schema.org/InStock',
    },
  ],
};

// Shared founder Person — used in /about route and BlogPosting.author
export const founderPersonJsonLd = {
  '@type': 'Person',
  'name': 'Lucas Spiller',
  'jobTitle': 'Founder',
  'url': 'https://lucasspiller.de/',
  'image': 'https://notionhabitheroes.com/assets/images/founder.jpg',
  'sameAs': ['https://www.linkedin.com/in/lucas-spiller/', 'https://lucasspiller.de/'],
  'worksFor': {
    '@type': 'Organization',
    'name': 'Notion Habit Heroes',
    'url': 'https://notionhabitheroes.com/',
  },
};

const aboutPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  'url': 'https://notionhabitheroes.com/about/',
  'name': 'About the Founder | Notion Habit Heroes',
  'mainEntity': founderPersonJsonLd,
};

// FAQPage schemas below are generated from the pool in shared/faq/faq-pool.ts (single source of truth).
const supportFaqJsonLd = faqPageJsonLd(SUPPORT_FAQ);

const pricingFaqJsonLd = faqPageJsonLd(PRICING_FAQ);

const homeFaqJsonLd = faqPageJsonLd(HOME_FAQ);

const templateFaqJsonLd = faqPageJsonLd(TEMPLATE_FAQ);

// WebPage schema for /template — datePublished is the page's original ship date; dateModified must be bumped on future content changes
const templateWebPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  'url': 'https://notionhabitheroes.com/template/',
  'name': 'Notion Habit Tracker Template with Automatic Streaks and Duels (Free)',
  'description': 'Notion habit tracker template that counts your streaks automatically, no formulas needed. Try the live demo, then connect Notion to get your free copy.',
  'datePublished': '2026-07-11',
  'dateModified': '2026-11-04',
  'primaryImageOfPage': {
    '@type': 'ImageObject',
    'url': 'https://notionhabitheroes.com/assets/images/widget/template-live_1240x734.webp',
    'width': 1240,
    'height': 734,
  },
};

// DigitalDocument schema for the template file itself, distinct from the WebPage describing it
const templateDigitalDocumentJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'DigitalDocument',
  'name': 'Notion Habit Tracker Template with Automatic Streaks and Duels',
  'description': 'Notion habit tracker template that counts your streaks automatically, no formulas needed. Try the live demo, then connect Notion to get your free copy.',
  'url': 'https://notionhabitheroes.com/template/',
  'isAccessibleForFree': true,
  'publisher': {
    '@type': 'Organization',
    'name': 'Notion Habit Heroes',
    'logo': {
      '@type': 'ImageObject',
      'url': 'https://notionhabitheroes.com/assets/images/logo-v4.png',
    },
  },
};

// FAQ answers must stay in sync with teams-landing.component.html
const teamsFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': [
    {
      '@type': 'Question',
      'name': 'How much does this cost?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'From €4 per seat per month. Onboarding is manual and invoicing is yearly. Talk to us for a quote based on your team size.',
      },
    },
    {
      '@type': 'Question',
      'name': "Where does our team's habit data live?",
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': "Each member's habit data lives in their own Notion workspace. We only read the one habit database each member chooses to connect.",
      },
    },
    {
      '@type': 'Question',
      'name': 'What do employees need to join?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'A free Notion account and our free habit tracker template. Nothing else to install.',
      },
    },
    {
      '@type': 'Question',
      'name': 'How long does setup take?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'About 15 minutes for the admin setting up the team space. About 5 minutes per member to connect their own Notion.',
      },
    },
    {
      '@type': 'Question',
      'name': 'Is there a minimum team size?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes, five people minimum. Below that a shared leaderboard does not add much.',
      },
    },
    {
      '@type': 'Question',
      'name': 'Can employees who already use Notion Habit Heroes personally join a team challenge?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes. Their personal streaks and history stay untouched. Joining a team challenge just adds the company leaderboard on top of what they already track.',
      },
    },
  ],
};

// FAQ answers must stay in sync with coaches-landing.component.html
const coachesFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': [
    {
      '@type': 'Question',
      'name': 'How much does this cost?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'From €29 per month for up to 10 clients. Onboarding is manual. Talk to us for a quote if you run a larger roster.',
      },
    },
    {
      '@type': 'Question',
      'name': "Can my clients see each other's data?",
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': "No. Each client's data lives in their own Notion workspace. They see exactly what they choose to connect. Clients never see each other.",
      },
    },
    {
      '@type': 'Question',
      'name': 'What do my clients see on their side?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Their own habit tracker plus any optional challenges you run. The roster dashboard with every client\'s streaks is coach-only.',
      },
    },
    {
      '@type': 'Question',
      'name': 'What do clients need to join?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'A free Notion account plus our free habit tracker template. Nothing else to install.',
      },
    },
    {
      '@type': 'Question',
      'name': 'Does this replace my coaching stack?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': "No. It covers daily habit adherence between sessions. It's not a substitute for your program content, scheduling or payments.",
      },
    },
    {
      '@type': 'Question',
      'name': 'Can a client stop sharing their data?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes, they can disconnect at any time. Their habit history stays in their own workspace either way.',
      },
    },
  ],
};

// Blog index JSON-LD — Blog entity + breadcrumb; individual posts carry their own BlogPosting schema
const blogIndexJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    'name': 'Notion Habit Heroes Blog',
    'url': 'https://notionhabitheroes.com/blog/',
    'description': 'Guides and ideas on habit building, gamification and getting more out of Notion — from the team behind Notion Habit Heroes.',
    'publisher': {
      '@type': 'Organization',
      'name': 'Notion Habit Heroes',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://notionhabitheroes.com/assets/images/logo-v4.png',
      },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://notionhabitheroes.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': 'https://notionhabitheroes.com/blog/' },
    ],
  },
];

const howItWorksPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  'url': 'https://notionhabitheroes.com/how-it-works/',
  'name': 'How Notion Habit Heroes Works',
  'description': 'How Notion Habit Heroes connects to your Notion workspace, updates every time you open the widget and turns your habit checkboxes into automatic streaks, points and competitions.',
  'isPartOf': {
    '@type': 'WebSite',
    'url': 'https://notionhabitheroes.com/',
  },
};

// Setup steps must stay in sync with how-to-use.component.html and how-to-use.component.ts
const setupStepsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  'name': 'How to set up Notion Habit Heroes',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Create a Free Account',
      'description': 'Sign up with your email address, or in one click with your Notion account.',
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Connect Your Notion',
      'description': 'Approve our Notion integration. It copies a ready-made habit tracker into your workspace, with your personal widget already on the page. Using your own habit database? Pick it during the connect; the setup page then gives you your widget link to paste onto any page.',
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': 'Track & Compete',
      'description': 'Check off your daily habits in Notion and watch your streaks grow in the widget. Join competitions and duels to stay motivated!',
    },
  ],
};

const WIDGET_PAGE_TITLE = 'Notion Habit Tracker Widget: Streaks, Leaderboards and Duels';
const WIDGET_PAGE_DESCRIPTION = 'Embed a free habit tracker widget in Notion: automatic streaks, a daily progress bar, completion rates, leaderboards and duels from your own checkboxes.';

// WebPage schema for /notion-habit-tracker-widget (#L055); bump dateModified on content changes
const widgetWebPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  'url': 'https://notionhabitheroes.com/notion-habit-tracker-widget/',
  'name': WIDGET_PAGE_TITLE,
  'description': WIDGET_PAGE_DESCRIPTION,
  'datePublished': '2026-10-24',
  'dateModified': '2026-10-24',
  'primaryImageOfPage': {
    '@type': 'ImageObject',
    'url': 'https://notionhabitheroes.com/assets/images/widget/streaks-widget.webp',
    'width': 751,
    'height': 563,
  },
};

const widgetFaqJsonLd = faqPageJsonLd(WIDGET_FAQ);

export const rootRouterConfig: Routes = [
  {
    path: '',
    loadComponent: () => import('./views/home/home-one.component').then(m => m.HomeOneComponent),
    data: {
      seo: {
        title: 'Notion Habit Heroes: Free Social Habit Tracker for Notion',
        description: 'Automatic streaks, competitions and 1-on-1 duels on the Notion habit tracker you already use. Free, no new app: connect your database and keep checking boxes in Notion.',
        jsonLd: [softwareAppJsonLd, homeFaqJsonLd],
      } as SeoData,
    },
  },
  {
    path: 'imprint',
    loadComponent: () => import('./views/legal/imprint/imprint.component').then(m => m.ImprintComponent),
    data: {
      seo: {
        title: 'Legal Imprint — Notion Habit Heroes',
        description: 'Legal imprint and contact information for Notion Habit Heroes.',
      } as SeoData,
    },
  },
  {
    path: 'terms',
    loadComponent: () => import('./views/legal/terms/terms.component').then(m => m.TermsComponent),
    data: {
      seo: {
        title: 'Terms of Service — Notion Habit Heroes',
        description: 'The terms of service that apply when you use Notion Habit Heroes.',
      } as SeoData,
    },
  },
  {
    path: 'privacy',
    loadComponent: () => import('./views/legal/privacy/privacy.component').then(m => m.PrivacyComponent),
    data: {
      seo: {
        title: 'Privacy Policy — Notion Habit Heroes',
        description: 'How Notion Habit Heroes collects, uses and protects your data.',
      } as SeoData,
    },
  },
  {
    path: 'support',
    loadComponent: () => import('./views/support/support.component').then(m => m.SupportComponent),
    data: {
      seo: {
        title: 'Support & FAQ — Notion Habit Heroes',
        description: 'Get help with Notion Habit Heroes. Find answers to common questions about connecting Notion, syncing habits, managing your subscription, and more.',
        jsonLd: [supportFaqJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'Support & FAQ', item: 'https://notionhabitheroes.com/support/' },
          ],
        }],
      } as SeoData,
    },
  },
  {
    path: 'start',
    loadComponent: () => import('./views/oauth/oauth.component').then(m => m.OauthComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Connect Notion — Notion Habit Heroes',
        description: 'Connect your Notion workspace to Notion Habit Heroes and start tracking habits with streaks, points and leaderboards.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'competition',
    loadComponent: () => import('./views/competition/competition.component').then(m => m.CompetitionComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Competition — Notion Habit Heroes',
        description: 'Compete with friends in habit challenges inside Notion. Track streaks, earn points and climb the leaderboard.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'profile',
    loadComponent: () => import('./views/profile/profile.component').then(m => m.ProfileComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Profile — Notion Habit Heroes',
        description: 'Manage your Notion Habit Heroes account, subscription and settings.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'groups',
    loadComponent: () => import('./views/groups/groups-list.component').then(m => m.GroupsListComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Your Groups — Notion Habit Heroes',
        description: 'See every Team or Coach space you belong to on Notion Habit Heroes.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'groups/:id',
    loadComponent: () => import('./views/groups/group-detail.component').then(m => m.GroupDetailComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Group — Notion Habit Heroes',
        description: 'View your Team or Coach space details on Notion Habit Heroes.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'feedback',
    loadComponent: () => import('./views/feedback/feedback.component').then(m => m.FeedbackComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Feedback — Notion Habit Heroes',
        description: 'Send a feature request, report a bug, or share your thoughts with the Notion Habit Heroes developer.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'login',
    loadComponent: () => import('./views/login/login.component').then(m => m.LoginComponent),
    canActivate: [LoggedInRedirectGuard],
    data: {
      seo: {
        title: 'Log In — Notion Habit Heroes',
        description: 'Log in to Notion Habit Heroes to manage your habit challenges, streaks and leaderboards in Notion.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'register',
    loadComponent: () => import('./views/auth/auth-form.component').then(m => m.AuthFormComponent),
    canActivate: [LoggedInRedirectGuard],
    data: {
      seo: {
        title: 'Create Your Free Account — Notion Habit Heroes',
        description: 'Create your free Notion Habit Heroes account and turn your Notion habit tracker into streaks, points and challenges with friends.',
      } as SeoData,
    },
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./views/auth/auth-form.component').then(m => m.AuthFormComponent),
    data: {
      seo: {
        title: 'Reset Password — Notion Habit Heroes',
        description: 'Reset your Notion Habit Heroes password.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'how-it-works',
    loadComponent: () => import('./views/howto/how-to-use.component').then(m => m.HowToUseComponent),
    data: {
      seo: {
        title: 'How It Works — Gamified Habit Tracking in Notion',
        description: 'Sign up, connect Notion, and the widget lands in your habit tracker by itself. Start your first habit challenge in minutes, with streaks, points and leaderboards included.',
        jsonLd: [howItWorksPageJsonLd, setupStepsJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'How It Works', item: 'https://notionhabitheroes.com/how-it-works/' },
          ],
        }],
      } as SeoData,
    },
  },
  {
    path: 'pricing',
    loadComponent: () => import('./views/pricing/pricing.component').then(m => m.PricingComponent),
    data: {
      seo: {
        title: 'Pricing — Free for Individuals | Notion Habit Heroes',
        description: 'Notion Habit Heroes is free for individuals: streaks, widgets, competitions and duels, no paid tier. Teams and Coaches plans are available for organizations.',
        jsonLd: [softwareAppJsonLd, pricingFaqJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://notionhabitheroes.com/pricing/' },
          ],
        }],
        ogImage: 'assets/images/og-pricing-v3.jpg',
      } as SeoData,
    },
  },
  {
    path: 'payment/success',
    loadComponent: () => import('./views/pricing/payment-success.component').then(m => m.PaymentSuccessComponent),
    data: {
      seo: {
        title: 'Payment Successful — Notion Habit Heroes',
        description: 'Your Notion Habit Heroes Pro subscription is now active. Enjoy competitions, leaderboards and advanced statistics.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'join',
    loadComponent: () => import('./views/join/join.component').then(m => m.JoinCompetitionComponent),
    data: {
      seo: {
        title: '🏆 You\'re invited to a habit competition!',
        description: 'A friend invited you to a habit competition on Notion Habit Heroes. Join in, keep your streaks alive and climb the leaderboard.',
        noindex: true,
        ogImage: 'assets/images/og-competition-invite-v3.jpg',
      } as SeoData,
    },
  },
  {
    path: 'duel',
    loadComponent: () => import('./views/duel/duel.component').then(m => m.DuelComponent),
    canActivate: [AuthGuard],
    data: {
      seo: {
        title: 'Duel — Notion Habit Heroes',
        description: 'Challenge a friend to a 1-on-1 habit duel. Track daily habit completion head-to-head and see who comes out on top.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'duel/join',
    loadComponent: () => import('./views/duel/duel-join.component').then(m => m.DuelJoinComponent),
    data: {
      seo: {
        title: '⚔️ You\'ve been challenged to a Habit Duel!',
        description: 'A friend wants to face you in a 1-on-1 habit duel. Accept the challenge on Notion Habit Heroes and see who keeps their streak going.',
        noindex: true,
        ogImage: 'assets/images/og-duel-invite-v3.jpg',
      } as SeoData,
    },
  },
  {
    // Folded into /duel/join (backlog #164): since !136 the duel-invite-preview
    // edge function rewrites OG tags per invite token for both routes, so the
    // static habit-only variant's sole reason to exist is gone. This keeps old
    // shared links (chat histories, etc.) working via a client-side redirect
    // that preserves the inviteToken (and any other) query params — the CDN
    // layer has its own 301 for the same path, see src/_redirects.
    path: 'duel/join/habit',
    pathMatch: 'full',
    redirectTo: (redirectData) => {
      const params = new URLSearchParams();
      const queryParams = redirectData.queryParams;
      // Object.keys is enough here. (Until the v21 upgrade tsconfig.json pinned
      // lib to es2016, which put Object.entries/values out of reach; the v21
      // migration dropped that pin, so lib now follows target es2022 and they
      // are available — this loop just has no reason to change.)
      for (const key of Object.keys(queryParams)) {
        const value = queryParams[key];
        if (value != null) {
          params.set(key, value as string);
        }
      }
      const qs = params.toString();
      return qs ? `/duel/join?${qs}` : '/duel/join';
    },
  },
  {
    path: 'group/join',
    loadComponent: () => import('./views/group/group-join.component').then(m => m.GroupJoinComponent),
    data: {
      seo: {
        title: 'You\'ve Been Invited to a Group! — Notion Habit Heroes',
        description: 'Join a Team or Coach group on Notion Habit Heroes and track your habits together on a shared leaderboard.',
        noindex: true,
      } as SeoData,
    },
  },
  {
    path: 'blog',
    loadComponent: () => import('./views/blog/blog.component').then(m => m.BlogComponent),
    data: {
      seo: {
        title: 'Blog — Habit Building & Notion Tips | Notion Habit Heroes',
        description: 'Guides and ideas on habit building, gamification and getting more out of Notion — from the team behind Notion Habit Heroes.',
        jsonLd: blogIndexJsonLd,
      } as SeoData,
    },
  },
  {
    path: 'blog/:slug',
    loadComponent: () => import('./views/blog/blog-article.component').then(m => m.BlogArticleComponent),
  },
  {
    path: 'season-0',
    loadComponent: () => import('./views/season-zero/season-zero.component').then(m => m.SeasonZeroComponent),
    data: {
      seo: {
        title: 'Earn the Founding Hero Badge in Season 0 — Notion Habit Heroes',
        description: 'The Founding Hero badge is awarded once, to everyone who finishes Season 0 (October 1 to 31, 2026). Free, one leaderboard, every finisher named in the hall of fame. The top scorer names Season 1.',
      } as SeoData,
    },
  },
  {
    path: 'season-1',
    loadComponent: () => import('./views/season-one/season-one.component').then(m => m.SeasonOneComponent),
    data: {
      seo: {
        title: 'Season 1 Starts January 1, 2027 — Notion Habit Heroes',
        description: 'Season 1 of Notion Habit Heroes runs January 1 to 31, 2027: a free 31-day habit competition with one leaderboard. Finishers earn the Season finisher badge. Connect your Notion habits now to be on the board from day one.',
      } as SeoData,
    },
  },
  {
    // The URL creator outreach links to (backlog #335). Not linked from the
    // header or footer, same as /season-0: it is an inbound landing page.
    // Attribution rides on the site-wide SignupSourceService capture in
    // AppComponent, so ?utm_source=<creator> needs nothing here.
    path: 'works-with',
    loadComponent: () => import('./views/works-with/works-with.component').then(m => m.WorksWithComponent),
    data: {
      seo: {
        title: 'Works with your Notion template | Notion Habit Heroes',
        description: 'Notion Habit Heroes plugs into the habit tracker template you already use: one row per day, a checkbox per habit. Streaks, widget and competitions without changing your template.',
      } as SeoData,
    },
  },
  {
    // Not /widgets: src/_headers marks /widgets/* noindex with the widget CSP; the sitemap skips it.
    path: 'notion-habit-tracker-widget',
    loadComponent: () => import('./views/widget-landing/widget-landing.component').then(m => m.WidgetLandingComponent),
    data: {
      seo: {
        title: WIDGET_PAGE_TITLE,
        description: WIDGET_PAGE_DESCRIPTION,
        ogImage: 'assets/images/og-default-v2.jpg',
        jsonLd: [widgetWebPageJsonLd, widgetFaqJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'Widget', item: 'https://notionhabitheroes.com/notion-habit-tracker-widget/' },
          ],
        }],
      } as SeoData,
    },
  },
  {
    path: 'about',
    loadComponent: () => import('./views/about/about.component').then(m => m.AboutComponent),
    data: {
      seo: {
        title: 'About the Founder | Notion Habit Heroes',
        description: 'Lucas Spiller is the solo developer behind Notion Habit Heroes, a social habit tracker inside Notion. Building since February 2023.',
        jsonLd: [aboutPageJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'About', item: 'https://notionhabitheroes.com/about/' },
          ],
        }],
      } as SeoData,
    },
  },
  {
    path: 'template',
    loadComponent: () => import('./views/template/template.component').then(m => m.TemplateComponent),
    data: {
      seo: {
        title: 'Notion Habit Tracker Template with Automatic Streaks and Duels (Free)',
        description: 'Notion habit tracker template that counts your streaks automatically, no formulas needed. Try the live demo, then connect Notion to get your free copy.',
        ogImage: 'assets/images/og-template-v3.jpg',
        jsonLd: [softwareAppJsonLd, templateWebPageJsonLd, templateDigitalDocumentJsonLd, templateFaqJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'Template', item: 'https://notionhabitheroes.com/template/' },
          ],
        }, {
          '@context': 'https://schema.org',
          '@type': 'VideoObject',
          'name': 'Notion habit tracker template: automatic streak tracking demo',
          'description': 'Demo tour of the free Notion habit tracker template connected to Notion Habit Heroes: streak bars at varied completion levels, the completion-rate card flip, a freeze day, the tavern pause and a perfect day celebrated with confetti.',
          'thumbnailUrl': 'https://notionhabitheroes.com/assets/videos/streak-flip-v1-poster.webp',
          'contentUrl': 'https://notionhabitheroes.com/assets/videos/streak-flip-v1.mp4',
          'uploadDate': '2026-08-01T00:00:00+02:00',
          'duration': 'PT25S',
        }],
      } as SeoData,
    },
  },
  {
    path: 'teams',
    loadComponent: () => import('./views/b2b/teams/teams-landing.component').then(m => m.TeamsLandingComponent),
    data: {
      seo: {
        title: 'Team Habit Challenges Inside Notion | Notion Habit Heroes',
        description: 'Run a wellness or habit challenge for your team inside Notion. Each member connects their own workspace; we handle the leaderboard. From €4 per seat per month.',
        ogImage: 'assets/images/og-teams-v3.jpg',
        jsonLd: [teamsFaqJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'Teams', item: 'https://notionhabitheroes.com/teams/' },
          ],
        }, {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Notion Habit Heroes for Teams',
          serviceType: 'Team habit tracking and engagement platform',
          description: 'Gamified habit tracking, streaks and friendly competitions for your whole team on top of the Notion workspace you already use.',
          provider: { '@type': 'Organization', name: 'Notion Habit Heroes', url: 'https://notionhabitheroes.com/' },
          url: 'https://notionhabitheroes.com/teams/',
          areaServed: 'Worldwide',
          offers: {
            '@type': 'Offer',
            url: 'https://notionhabitheroes.com/teams/',
            priceCurrency: 'EUR',
            price: '4',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              priceCurrency: 'EUR',
              price: '4',
              unitText: 'per seat per month',
            },
            availability: 'https://schema.org/InStock',
          },
        }],
      } as SeoData,
    },
  },
  {
    path: 'coaches',
    loadComponent: () => import('./views/b2b/coaches/coaches-landing.component').then(m => m.CoachesLandingComponent),
    data: {
      seo: {
        title: 'Client Accountability Dashboard for Notion Coaches',
        description: "Track every client's Notion habit streaks from one coach dashboard and catch drop-off before it becomes a no-show. From €29 per month for up to 10 clients.",
        ogImage: 'assets/images/og-coaches-v3.jpg',
        jsonLd: [coachesFaqJsonLd, {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://notionhabitheroes.com/' },
            { '@type': 'ListItem', position: 2, name: 'Coaches', item: 'https://notionhabitheroes.com/coaches/' },
          ],
        }, {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Notion Habit Heroes for Coaches',
          serviceType: 'Client habit tracking and accountability platform for coaches',
          description: 'Automatic streaks, points and progress tracking for your coaching clients on top of the Notion habit tracker they already use, with no data shared between clients.',
          provider: { '@type': 'Organization', name: 'Notion Habit Heroes', url: 'https://notionhabitheroes.com/' },
          url: 'https://notionhabitheroes.com/coaches/',
          areaServed: 'Worldwide',
          offers: {
            '@type': 'Offer',
            url: 'https://notionhabitheroes.com/coaches/',
            priceCurrency: 'EUR',
            price: '29',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              priceCurrency: 'EUR',
              price: '29',
              unitText: 'per month for up to 10 clients',
            },
            availability: 'https://schema.org/InStock',
          },
        }],
      } as SeoData,
    },
  },
  {
    path: '404',
    loadComponent: () => import('./views/not-found/not-found.component').then(m => m.NotFoundComponent),
    data: { seo: notFoundSeo },
  },
  {
    path: '**',
    loadComponent: () => import('./views/not-found/not-found.component').then(m => m.NotFoundComponent),
    data: { seo: notFoundSeo },
  },
];
